
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, CartesianGrid } from 'recharts';
import { MonthlyMetric } from './types';

interface MetricsChartProps {
  data: MonthlyMetric[];
  title: string;
  valueFormatter: (value: number) => string;
  color: string;
  loading?: boolean;
}

const MONTHS_SHORT = [
  'Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн',
  'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'
];

export const MetricsChart: React.FC<MetricsChartProps> = ({
  data,
  title,
  valueFormatter,
  color,
  loading = false
}) => {
  const chartData = data.map(item => ({
    ...item,
    monthLabel: `${MONTHS_SHORT[item.month - 1]} ${item.year.toString().slice(-2)}`
  }));

  const chartConfig = {
    value: {
      label: title,
      color: color.includes('hsl') ? color : `hsl(var(--chart-1))`,
    },
  };

  // Логирование для отладки
  console.log(`График "${title}": получено ${data.length} точек данных`, chartData);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title} - Динамика</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] bg-muted rounded animate-pulse"></div>
        </CardContent>
      </Card>
    );
  }

  if (!data.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title} - Динамика</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            Нет данных для отображения
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title} - Динамика</CardTitle>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[300px]">
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis 
              dataKey="monthLabel" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12 }}
              interval={0}
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12 }}
              tickFormatter={valueFormatter}
              width={80}
            />
            <ChartTooltip 
              content={
                <ChartTooltipContent 
                  formatter={(value: any) => [valueFormatter(value), title]}
                />
              }
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={`var(--color-value)`}
              strokeWidth={3}
              fill={`var(--color-value)`}
              dot={{ 
                r: 4, 
                fill: `var(--color-value)`, 
                strokeWidth: 2, 
                stroke: 'white' 
              }}
              activeDot={{ 
                r: 6, 
                strokeWidth: 2, 
                fill: `var(--color-value)`,
                stroke: 'white'
              }}
              connectNulls={true}
            />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};
